import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LEAD } from "@/data/content";
import { Eyebrow } from "./ui";
import { Reveal } from "./Reveal";
import { getUtm } from "@/lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-sm border border-brand-bordeaux/20 bg-brand-cream px-4 py-3 text-sm text-brand-graphite placeholder:text-brand-graphite/40 focus:border-brand-bordeaux focus:outline-none focus:ring-1 focus:ring-brand-bordeaux transition-colors";

function LabeledInput({ id, label, required, type = "text", ...props }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-graphite/85 mb-2">
        {label} {required && <span className="text-brand-bordeaux">*</span>}
      </label>
      <input id={id} name={id} type={type} required={required} className={inputCls} data-testid={`lead-${id}`} {...props} />
    </div>
  );
}

export default function LeadFormSection() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleChannel = (c) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Devi accettare la privacy policy per continuare.");
      return;
    }
    const f = e.target;
    setSubmitting(true);
    const utm = getUtm();
    const payload = {
      first_name: f.first_name.value,
      last_name: f.last_name.value,
      company: f.company.value,
      role: f.role.value,
      email: f.email.value,
      phone: f.phone.value,
      website: f.website.value,
      company_size: f.company_size.value,
      revenue_band: f.revenue_band.value,
      sales_channels: channels,
      software_stack: f.software_stack.value,
      main_problem: f.main_problem.value,
      privacy_consent: consent,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_content: utm.utm_content,
      utm_term: utm.utm_term,
      landing_page: utm.landing_page || window.location.href,
    };

    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("bad response");
      if (window.posthog) window.posthog.capture("lead_submit", { company: payload.company });
      navigate("/thank-you");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici via email.");
      setSubmitting(false);
    }
  };

  return (
    <section id="lead" className="relative bg-brand-bordeaux py-20 lg:py-32 grain-overlay">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>{LEAD.eyebrow}</Eyebrow></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-brand-ivory whitespace-pre-line">
                {LEAD.title}
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 text-base leading-relaxed text-brand-ivory/75 max-w-md">{LEAD.subtitle}</p>
            </Reveal>
          </div>

          <Reveal delay={1} className="lg:col-span-7">
            <form onSubmit={onSubmit} className="rounded-sm bg-brand-ivory p-6 sm:p-8 lg:p-10 space-y-6" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <LabeledInput id="first_name" label="Nome" required />
                <LabeledInput id="last_name" label="Cognome" required />
                <LabeledInput id="company" label="Azienda / Cantina" required />
                <LabeledInput id="role" label="Ruolo" />
                <LabeledInput id="email" label="Email aziendale" type="email" required />
                <LabeledInput id="phone" label="Telefono (opzionale)" type="tel" />
                <LabeledInput id="website" label="Sito web" type="url" placeholder="https://" />
                <LabeledInput id="company_size" label="Bottiglie / anno (indicativo)" placeholder="es. 250.000" />
              </div>

              <div>
                <label htmlFor="revenue_band" className="block text-sm font-medium text-brand-graphite/85 mb-2">
                  Fatturato aziendale (opzionale)
                </label>
                <select id="revenue_band" name="revenue_band" className={inputCls} data-testid="lead-revenue_band" defaultValue="">
                  <option value="">Preferisco non indicare</option>
                  {LEAD.revenueBands.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div>
                <span className="block text-sm font-medium text-brand-graphite/85 mb-3">Canali utilizzati</span>
                <div className="flex flex-wrap gap-2">
                  {LEAD.channels.map((c) => {
                    const active = channels.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleChannel(c)}
                        data-testid={`lead-channel-${c.toLowerCase().replace(/\.|\s/g, "-")}`}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                          active
                            ? "border-brand-bordeaux bg-brand-bordeaux text-brand-ivory"
                            : "border-brand-bordeaux/25 text-brand-graphite/70 hover:border-brand-bordeaux"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <LabeledInput id="software_stack" label="Software utilizzati" placeholder="CRM, ecommerce, gestionale, mailing…" />

              <div>
                <label htmlFor="main_problem" className="block text-sm font-medium text-brand-graphite/85 mb-2">
                  Qual è oggi il processo commerciale che vorresti migliorare?
                </label>
                <textarea
                  id="main_problem"
                  name="main_problem"
                  rows={4}
                  className={inputCls}
                  data-testid="lead-main_problem"
                  placeholder="Raccontaci in poche righe…"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="lead-consent"
                  className="mt-1 h-4 w-4 accent-brand-bordeaux"
                />
                <span className="text-xs leading-relaxed text-brand-graphite/70">{LEAD.privacy}</span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="w-full rounded-full bg-brand-bordeaux px-7 py-4 text-sm font-semibold text-brand-ivory transition-all hover:bg-brand-wine disabled:opacity-60"
              >
                {submitting ? "Invio in corso…" : LEAD.cta}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
